import {
  type StudentGrade,
  gradeLevel,
  pointsAverage,
  shortDate,
  termFor,
  todayIso,
  useStudentRecord,
  useTerms,
} from '@shkola/api';
import { subjectLook } from '@shkola/design';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Block,
  GRAD,
  type Grad,
  Head,
  INK,
  INK_FAINT,
  INK_SOFT,
  LINE,
  PAPER,
  PageHead,
  SUBJECT_GRAD,
  SubjectArt,
  Tabs,
  grad,
} from '../design/forms';
import {
  CaretDownIcon,
  ChartBarIcon,
  ChatCircleIcon,
  type FilledIcon,
  HeartIcon,
  LightbulbIcon,
  MedalIcon,
  StarIcon,
} from '../design/icons';
import { gradeShort } from '../lib/labels';
import type { StudentContext } from './home/model';

/*
 * «Оцінки» of the student in «Форми 2» (28.10, after the home). The title on the paper with the term to
 * choose; on the left the average big and light with how the grades spread (twelve bars in the colours of
 * the levels) and the teachers' words; on the right every subject as a row — its picture, the grades as
 * small marks written by hand in their level's colour (as on the home), the average; a row opens to the
 * grades with their dates and what for.
 */

/** the colour of a level: low coral, then butter, sky, lime; thematic and term grades lavender */
const LEVEL: ReadonlyArray<Grad> = ['coral', 'butter', 'sky', 'lime'];
const FINAL = new Set<StudentGrade['kind']>(['thematic', 'semester', 'year']);

const toneOf = (g: StudentGrade): Grad =>
  FINAL.has(g.kind) ? 'lavender' : g.value ? (LEVEL[gradeLevel(g.value)] ?? 'stone') : 'stone';

/** the teachers' words by their kind: a colour and a small icon */
const FEEDBACK: Record<string, { tone: Grad; icon: FilledIcon }> = {
  star: { tone: 'butter', icon: StarIcon },
  progress: { tone: 'sky', icon: ChartBarIcon },
  effort: { tone: 'coral', icon: MedalIcon },
  idea: { tone: 'lime', icon: LightbulbIcon },
  help: { tone: 'pink', icon: HeartIcon },
};

const decimal = (n: number | null) => (n !== null ? String(n).replace('.', ',') : '—');

/** A grade as a small mark on its level's colour. */
function GradeMark({ grade, size = 'md' }: { grade: StudentGrade; size?: 'md' | 'sm' }) {
  const { t } = useTranslation();
  const tone = toneOf(grade);
  return (
    <span
      title={`${shortDate(grade.date)} · ${t(`journal.kinds.${grade.kind}`)}${grade.topic ? ` · ${grade.topic}` : ''}`}
      className={`inline-flex shrink-0 items-center justify-center rounded-[10px] px-1.5 leading-none font-semibold tabular-nums ${size === 'sm' ? 'h-8 min-w-8 text-[15px]' : 'h-9 min-w-9 text-[16px]'}`}
      style={{ backgroundImage: grad(tone), color: INK }}
    >
      {gradeShort(t, grade.value, grade.special)}
    </span>
  );
}

/** How many of each grade: twelve bars in the colours of the levels. */
function Spread({ grades }: { grades: StudentGrade[] }) {
  const counts = Array.from({ length: 12 }, () => 0);
  for (const g of grades) {
    if (g.value?.scale === 'points12')
      counts[g.value.points - 1] = (counts[g.value.points - 1] ?? 0) + 1;
  }
  const max = Math.max(1, ...counts);
  return (
    <div className="mt-6 grid grid-cols-12 gap-1.5">
      {counts.map((n, i) => {
        const tone = LEVEL[Math.min(3, Math.floor(i / 3))] as Grad;
        return (
          <div key={i} className="flex flex-col items-center gap-1.5">
            <div className="flex h-20 w-full items-end">
              <span
                title={String(n)}
                className="block w-full rounded-[6px]"
                style={
                  n
                    ? {
                        height: `${Math.max(8, (n / max) * 100)}%`,
                        backgroundImage: `linear-gradient(180deg, ${GRAD[tone].to} 0%, ${GRAD[tone].from} 100%)`,
                      }
                    : { height: '8%', backgroundColor: PAPER }
                }
              />
            </div>
            <span className="text-[11px] font-medium" style={{ color: INK_FAINT }}>
              {i + 1}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** One subject: its picture, the grades by hand, the average; opens to every grade with its day. */
function SubjectRow({
  subject,
  grades,
  missed,
}: {
  subject: string;
  grades: StudentGrade[];
  missed: number;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const look = subjectLook(subject);
  const average = pointsAverage(grades);
  return (
    <li style={{ borderTop: `1px solid ${LINE}` }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="group flex w-full flex-wrap items-center gap-x-4 gap-y-2 py-3 text-left"
      >
        <SubjectArt icon={look.icon} tone={SUBJECT_GRAD[look.tone]} size={40} />
        <span className="min-w-0 flex-1 sm:w-44 sm:flex-none">
          <span className="block truncate text-[16px] leading-5 font-medium" style={{ color: INK }}>
            {subject}
          </span>
          {missed > 0 && (
            <span className="text-[12.5px]" style={{ color: INK_SOFT }}>
              {t('grades.absencesCount', { count: missed })}
            </span>
          )}
        </span>
        <span className="order-last flex min-w-0 basis-full flex-wrap gap-1.5 pl-14 sm:order-none sm:basis-0 sm:flex-1 sm:pl-0">
          {grades.map((g) => (
            <GradeMark key={g.id} grade={g} size="sm" />
          ))}
        </span>
        <span
          className="w-14 shrink-0 text-right font-display text-[28px] font-light tracking-[-0.03em]"
          style={{ color: INK }}
        >
          {decimal(average)}
        </span>
        <CaretDownIcon
          size={13}
          weight="bold"
          aria-hidden
          color={INK}
          className={`shrink-0 transition-transform duration-300 ease-spring ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul className="motion-rise flex flex-col gap-2 pb-4 pl-14">
          {[...grades].reverse().map((g) => (
            <li key={g.id} className="flex items-center gap-3 text-[13.5px]" style={{ color: INK }}>
              <GradeMark grade={g} size="sm" />
              <span className="min-w-0">
                {shortDate(g.date)} · {t(`journal.kinds.${g.kind}`)}
                <span style={{ color: INK_SOFT }}>
                  {g.topic ? ` · ${g.topic}` : g.title ? ` · ${g.title}` : ''}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function ShapesGrades({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const terms = useTerms(ctx.schoolId);
  const [termId, setTermId] = useState<string>();
  const term =
    terms.data?.find((x) => x.id === termId) ?? (terms.data && termFor(terms.data, todayIso()));
  const record = useStudentRecord(ctx.child.memberId, term?.id);
  const grades = record.data?.grades ?? [];
  const absences = record.data?.absences ?? [];
  const formative = record.data?.formative ?? [];
  const average = pointsAverage(grades);
  const bySubject = new Map<string, StudentGrade[]>();
  for (const g of grades) bySubject.set(g.subject, [...(bySubject.get(g.subject) ?? []), g]);
  const subjects = [...bySubject.keys()].sort((a, b) => a.localeCompare(b, 'uk'));

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('grades.title')}>
        {terms.data && terms.data.length > 1 && (
          <Tabs
            label={t('journal.term', { n: '' })}
            value={term?.id}
            onChange={setTermId}
            options={terms.data.map((x) => ({
              id: x.id,
              label: t('journal.term', { n: x.number }),
            }))}
          />
        )}
      </PageHead>

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-[380px_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-4">
          <Block>
            <p
              className="text-[14px] font-medium first-letter:uppercase"
              style={{ color: INK_SOFT }}
            >
              {t('student.stickers.average')}
            </p>
            <p
              className="font-display text-[88px] leading-[92px] font-light tracking-[-0.05em]"
              style={{ color: INK }}
            >
              {decimal(average)}
            </p>
            <p className="mt-1 flex flex-wrap gap-1.5">
              <span
                className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[13px] font-medium"
                style={{ backgroundColor: PAPER, color: INK }}
              >
                {t('student.grades.count', { count: grades.length })}
              </span>
              <span
                className="inline-flex h-7 items-center rounded-[8px] px-2.5 text-[13px] font-medium"
                style={{ backgroundColor: PAPER, color: INK }}
              >
                {t('grades.absencesCount', { count: absences.length })}
              </span>
            </p>
            <Spread grades={grades} />
          </Block>

          {formative.length > 0 && (
            <Block>
              <Head title={t('grades.formative')} />
              <ul className="flex flex-col gap-4">
                {formative.slice(0, 5).map((f) => {
                  const kind = (f.sticker && FEEDBACK[f.sticker]) || {
                    tone: 'lavender' as Grad,
                    icon: ChatCircleIcon,
                  };
                  const Icon = kind.icon;
                  return (
                    <li key={f.id} className="flex gap-3">
                      <span
                        className="flex size-9 shrink-0 items-center justify-center rounded-full"
                        style={{ backgroundImage: grad(kind.tone), color: GRAD[kind.tone].ink }}
                      >
                        <Icon size={16} weight="fill" aria-hidden />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[12.5px]" style={{ color: INK_SOFT }}>
                          {f.subject} · {shortDate(f.date)}
                        </span>
                        {f.comment && (
                          <span className="block text-[15px] leading-[22px]" style={{ color: INK }}>
                            {f.comment}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Block>
          )}
        </div>

        <Block>
          <Head title={t('grades.bySubject')} />
          {subjects.length === 0 && !record.isPending ? (
            <p style={{ color: INK_SOFT }}>{t('grades.noGrades')}</p>
          ) : (
            <ul className="flex flex-col">
              {subjects.map((s) => (
                <SubjectRow
                  key={s}
                  subject={s}
                  grades={[...(bySubject.get(s) ?? [])].sort((a, b) =>
                    a.date.localeCompare(b.date),
                  )}
                  missed={absences.filter((a) => a.subject === s).length}
                />
              ))}
            </ul>
          )}
        </Block>
      </div>
    </div>
  );
}
